import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Checkbox,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import axios from "axios";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { LoadingButton } from "@mui/lab";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useLogin } from "providers/Login";
import { useNotification } from "providers/Notification";
import DataTable, { useTableColumnState } from "components/Table";
import { Directory, directoryDisplayNames } from "./_components/Types";
import * as directoriesApi from "./_components/directoriesApi";
import * as groupsApi from "./_components/directoryGroupsApi";

type Columns = "included" | "name";

export default function DirectoryGroups() {
    const { id = "" } = useParams();
    return <DirectoryGroupsPage key={id} id={id} />;
}

function DirectoryGroupsPage({ id }: { id: string }) {
    const navigate = useNavigate();
    const login = useLogin();
    const notification = useNotification();
    const [req] = useState(() => login.GetAxios());
    const requestSequence = useRef(0);
    const [directory, setDirectory] = useState<Directory>();
    const [groups, setGroups] = useState<groupsApi.DirectoryGroup[]>([]);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [take, setTake] = useState(25);
    const [cursorHistory, setCursorHistory] = useState([""]);
    const [pageIndex, setPageIndex] = useState(0);
    const [nextCursor, setNextCursor] = useState("");
    const [includedCount, setIncludedCount] = useState(0);
    // undefined means the provider did not report a total, which is different
    // from a total of zero - see DirectoryGroupsPage.total.
    const [total, setTotal] = useState<number | undefined>(undefined);
    // The directory is too large for its DC to sort. Not an error: the listing
    // is fine once narrowed, or in directory order.
    const [searchRequired, setSearchRequired] = useState(false);
    const [sortBy, setSortBy] = useState<groupsApi.DirectoryGroupsSort>(groupsApi.defaultDirectoryGroupsSort);
    const [filters, setFilters] = useState<groupsApi.DirectoryGroupFilters>(groupsApi.defaultDirectoryGroupFilters);
    // Advanced mode is the admin's own filter expression instead of the search
    // box and the three selects. The two never intersect: switching either way
    // clears whatever the other mode had.
    const [advancedMode, setAdvancedMode] = useState(false);
    const [advancedFilter, setAdvancedFilter] = useState("");
    const [appliedAdvancedFilter, setAppliedAdvancedFilter] = useState<string>();
    const [translation, setTranslation] = useState<groupsApi.GroupFilterTranslation>();
    const translationSequence = useRef(0);
    const [reloadToken, setReloadToken] = useState(0);
    const [loading, setLoading] = useState(true);
    const [pendingChanges, setPendingChanges] = useState<Record<string, groupsApi.DirectoryGroup>>({});
    const pendingChangesRef = useRef<Record<string, groupsApi.DirectoryGroup>>({});
    const [replaceWith, setReplaceWith] = useState<boolean | undefined>();
    const replaceWithRef = useRef<boolean | undefined>();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [columnsState] = useTableColumnState<Columns>([
        { columnId: "included", width: "12%" },
        { columnId: "name", width: "88%" },
    ]);

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedSearch(search), 400);
        return () => window.clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        const sequence = ++translationSequence.current;
        setTranslation(undefined);
        if (!req || !advancedMode || advancedFilter.trim() === "") {
            return;
        }
        const timer = window.setTimeout(() => {
            groupsApi.translateGroupFilter(req, advancedFilter)
                .then((result) => {
                    if (sequence === translationSequence.current) {
                        setTranslation(result);
                    }
                })
                .catch(() => {
                    if (sequence === translationSequence.current) {
                        setTranslation({ error: { message: "Could not check this filter.", offset: -1 } });
                    }
                });
        }, 400);
        return () => window.clearTimeout(timer);
    }, [advancedMode, advancedFilter, req]);

    useEffect(() => {
        setCursorHistory((current) => current.length === 1 && current[0] === "" ? current : [""]);
        setPageIndex(0);
    }, [debouncedSearch, take, sortBy, filters.nameMatch, filters.category, filters.scope, appliedAdvancedFilter]);

    const loadGroups = async () => {
        if (!req || !id || !directory) {
            return;
        }
        const advancedParam = advancedMode && groupsApi.directorySupportsAdvancedFilter[directory.type]
            ? appliedAdvancedFilter
            : undefined;
        if (advancedMode && advancedParam === undefined) {
            requestSequence.current++;
            setError("");
            setGroups([]);
            setNextCursor("");
            setTotal(undefined);
            setSearchRequired(false);
            setLoading(false);
            return;
        }
        const sequence = ++requestSequence.current;
        setLoading(true);
        setError("");
        try {
            // Only send a sort or a filter for providers that honour them, so the
            // request never implies support the backend does not have.
            const sortParam = groupsApi.directorySupportsSorting[directory.type] ? sortBy : undefined;
            const filterParams = advancedParam !== undefined || !groupsApi.directorySupportsGroupFilters[directory.type]
                ? undefined
                : filters;
            const page = await groupsApi.listDirectoryGroups(
                req, id, cursorHistory[pageIndex] || "", take,
                advancedParam !== undefined ? "" : debouncedSearch, sortParam, filterParams, advancedParam,
            );
            if (sequence === requestSequence.current) {
                setGroups((page.groups || []).map((group) => ({
                    ...group,
                    included: pendingChangesRef.current[group.id]?.included
                        ?? replaceWithRef.current
                        ?? group.included,
                })));
                setNextCursor(page.nextCursor || "");
                setIncludedCount(page.includedCount || 0);
                setTotal(page.total);
                setSearchRequired(page.searchRequired === true);
            }
        } catch (err) {
            console.error(err);
            if (sequence === requestSequence.current) {
                const reason = axios.isAxiosError(err)
                    ? String((err.response?.data as { message?: string } | undefined)?.message || "")
                    : "";
                setError(reason || "Could not load groups from this directory.");
            }
        } finally {
            if (sequence === requestSequence.current) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!req || !id) {
            return;
        }
        directoriesApi.listDirectories(req).then((directories) => {
            const current = directories.find((item) => item.id === id);
            if (!current) {
                navigate("/directory", { replace: true });
                return;
            }
            if (current.type === "ldap" && directories.some((item) => item.parentId === current.id)) {
                navigate("/directory", { replace: true });
                return;
            }
            setDirectory(current);
        }).catch(() => setError("Could not load this directory."));
    }, [id, navigate, req]);

    useEffect(() => {
        loadGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, directory?.id, pageIndex, cursorHistory, take, debouncedSearch, sortBy,
        filters.nameMatch, filters.category, filters.scope, advancedMode, appliedAdvancedFilter, reloadToken]);

    const nextPage = () => {
        if (!nextCursor) {
            return;
        }
        const nextIndex = pageIndex + 1;
        setCursorHistory((current) => {
            const updated = current.slice(0, nextIndex);
            updated[nextIndex] = nextCursor;
            return updated;
        });
        setPageIndex(nextIndex);
    };

    const setIncluded = (group: groupsApi.DirectoryGroup, included: boolean) => {
        const change = { ...group, included };
        const updated = { ...pendingChangesRef.current, [group.id]: change };
        pendingChangesRef.current = updated;
        setPendingChanges(updated);
        setGroups((current) => current.map((item) => item.id === group.id ? change : item));
    };

    const setAllIncluded = (included: boolean) => {
        replaceWithRef.current = included;
        setReplaceWith(included);
        pendingChangesRef.current = {};
        setPendingChanges({});
        setGroups((current) => current.map((group) => ({ ...group, included })));
    };

    const saveGroups = async () => {
        if (!req) {
            return;
        }
        setSaving(true);
        try {
            const sortParam = directory && groupsApi.directorySupportsSorting[directory.type] ? sortBy : undefined;
            await groupsApi.saveDirectoryGroups(
                req, id, Object.values(pendingChangesRef.current), replaceWithRef.current, sortParam,
            );
            pendingChangesRef.current = {};
            setPendingChanges({});
            replaceWithRef.current = undefined;
            setReplaceWith(undefined);
            setReloadToken((current) => current + 1);
            notification.Display({
                title: "Success",
                message: "Directory groups saved.",
                type: "success",
            });
        } catch (err) {
            console.error(err);
            // The bulk walk refuses a replacement it cannot see every page of, and
            // says why — a generic message would hide the one actionable part.
            const message = err instanceof Error && err.message ? err.message : "Could not save directory groups.";
            notification.Display({ title: "Error", message, type: "error" });
        } finally {
            setSaving(false);
        }
    };

    const hasPendingChanges = replaceWith !== undefined || Object.keys(pendingChanges).length > 0;
    const supportsFilters = directory !== undefined && groupsApi.directorySupportsGroupFilters[directory.type];
    const filtersActive = supportsFilters && groupsApi.hasActiveGroupFilters(filters);
    const supportsAdvanced = directory !== undefined && groupsApi.directorySupportsAdvancedFilter[directory.type];
    const totalIsNarrowed = debouncedSearch !== "" || filtersActive
        || (advancedMode && appliedAdvancedFilter !== undefined);
    const translationFailed = translation?.error !== undefined;
    const canRunAdvancedQuery = advancedFilter.trim() !== ""
        && !translationFailed
        && translation?.ldapFilter !== undefined;

    const setFilter = <K extends keyof groupsApi.DirectoryGroupFilters>(
        key: K,
        value: groupsApi.DirectoryGroupFilters[K],
    ) => setFilters((current) => ({ ...current, [key]: value }));

    const switchMode = (advanced: boolean) => {
        if (advanced === advancedMode) {
            return;
        }
        setAdvancedMode(advanced);
        setSearch("");
        setDebouncedSearch("");
        setFilters(groupsApi.defaultDirectoryGroupFilters);
        setAdvancedFilter("");
        setAppliedAdvancedFilter(undefined);
        setTranslation(undefined);
        setCursorHistory([""]);
        setPageIndex(0);
    };

    const runAdvancedQuery = () => {
        setAppliedAdvancedFilter(advancedFilter);
        setCursorHistory([""]);
        setPageIndex(0);
        setReloadToken((current) => current + 1);
    };

    // "Include all"/"Exclude all" replace the whole inclusion list, which means
    // walking every page first. A search or a filter narrows the walk, and
    // searchRequired means the DC will not serve the walk at all — replacing on any
    // of them would save a list built from groups nobody could see.
    const bulkDisabled = saving || debouncedSearch !== "" || filtersActive || searchRequired || advancedMode;
    const bulkDisabledReason = advancedMode
        ? "Switch back to simple filtering to change every group"
        : debouncedSearch
            ? "Clear the search to change every group"
            : filtersActive
                ? "Clear the category and scope filters to change every group"
                : searchRequired
                    ? "This directory is too large to sort; switch to the directory order first"
                    : undefined;

    return <>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/directory")} sx={{ marginBottom: "16px" }}>
            Back to directories
        </Button>
        <Box display="flex" alignItems="start" gap="16px" sx={{ marginBottom: "22px" }}>
            <Box>
                <Typography variant="h1">Groups in {directory?.name || "directory"}</Typography>
                {directory && <Typography variant="body2" color="text.secondary" sx={{ marginTop: "6px" }}>
                    {directoryDisplayNames[directory.type]} · {hasPendingChanges
                        ? "Unsaved changes"
                        : `${includedCount} groups included`}
                    {total !== undefined && ` · ${total.toLocaleString()} ${totalIsNarrowed ? "matching" : "in directory"}`}
                </Typography>}
            </Box>
            <Box display="flex" gap="8px" sx={{ marginLeft: "auto" }}>
                <Button
                    disabled={bulkDisabled}
                    title={bulkDisabledReason}
                    variant="outlined"
                    onClick={() => setAllIncluded(false)}
                >
                    Exclude all
                </Button>
                <Button
                    disabled={bulkDisabled}
                    title={bulkDisabledReason}
                    variant="outlined"
                    onClick={() => setAllIncluded(true)}
                >
                    Include all
                </Button>
                <LoadingButton
                    id="save-directory-groups-button"
                    loading={saving}
                    disabled={!hasPendingChanges}
                    variant="contained"
                    onClick={saveGroups}
                >
                    Save
                </LoadingButton>
            </Box>
        </Box>

        <Box display="flex" alignItems="center" gap="12px" flexWrap="wrap" sx={{ marginBottom: "14px" }}>
            <Typography color="text.secondary">
                Choose which groups from this directory can be used in policies.
            </Typography>
            <Box display="flex" alignItems="center" gap="12px" flexWrap="wrap" sx={{ marginLeft: "auto" }}>
                {supportsAdvanced && <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={advancedMode ? "advanced" : "simple"}
                    onChange={(_, value) => value !== null && switchMode(value === "advanced")}
                    aria-label="Filter mode"
                >
                    <ToggleButton value="simple">Simple</ToggleButton>
                    <ToggleButton value="advanced">Advanced</ToggleButton>
                </ToggleButtonGroup>}
                {!advancedMode && <>
                <FormControl variant="outlined" size="small" sx={{ width: "320px" }}>
                    <InputLabel htmlFor="directory-groups-search">Search groups</InputLabel>
                    <OutlinedInput
                        id="directory-groups-search"
                        label="Search groups"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        endAdornment={search && <InputAdornment position="end">
                            <Button aria-label="Clear search" onClick={() => setSearch("")} sx={{ minWidth: 0 }}>
                                <ClearIcon fontSize="small" />
                            </Button>
                        </InputAdornment>}
                    />
                </FormControl>
                {supportsFilters && <>
                    <FormControl size="small" sx={{ minWidth: "150px" }}>
                        <InputLabel id="directory-groups-name-match-label">Name match</InputLabel>
                        <Select
                            labelId="directory-groups-name-match-label"
                            label="Name match"
                            value={filters.nameMatch}
                            onChange={(event) => setFilter("nameMatch", event.target.value as groupsApi.DirectoryGroupsNameMatch)}
                        >
                            <MenuItem value="contains">Contains</MenuItem>
                            <MenuItem value="prefix">Starts with</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: "160px" }}>
                        <InputLabel id="directory-groups-category-label">Category</InputLabel>
                        <Select
                            labelId="directory-groups-category-label"
                            label="Category"
                            value={filters.category}
                            onChange={(event) => setFilter("category", event.target.value as groupsApi.DirectoryGroupsCategory)}
                        >
                            <MenuItem value="any">Any category</MenuItem>
                            <MenuItem value="security">Security</MenuItem>
                            <MenuItem value="distribution">Distribution</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: "160px" }}>
                        <InputLabel id="directory-groups-scope-label">Scope</InputLabel>
                        <Select
                            labelId="directory-groups-scope-label"
                            label="Scope"
                            value={filters.scope}
                            onChange={(event) => setFilter("scope", event.target.value as groupsApi.DirectoryGroupsScope)}
                        >
                            <MenuItem value="any">Any scope</MenuItem>
                            <MenuItem value="global">Global</MenuItem>
                            <MenuItem value="universal">Universal</MenuItem>
                            <MenuItem value="domain_local">Domain local</MenuItem>
                        </Select>
                    </FormControl>
                </>}
                </>}
                {directory && groupsApi.directorySupportsSorting[directory.type] && <FormControl size="small" sx={{ minWidth: "210px" }}>
                    <InputLabel id="directory-groups-sort-label">Sort by</InputLabel>
                    <Select
                        labelId="directory-groups-sort-label"
                        label="Sort by"
                        value={sortBy}
                        onChange={(event) => setSortBy(event.target.value as groupsApi.DirectoryGroupsSort)}
                    >
                        <MenuItem value="default">Directory order</MenuItem>
                        <MenuItem value="name">Name (A–Z)</MenuItem>
                        <MenuItem value="recent">Recent first (newest created)</MenuItem>
                    </Select>
                </FormControl>}
            </Box>
        </Box>

        {advancedMode && supportsAdvanced && <Box sx={{ marginBottom: "14px" }}>
            <Box display="flex" alignItems="flex-start" gap="12px" flexWrap="wrap">
                <FormControl variant="outlined" size="small" sx={{ flex: 1, minWidth: "360px" }}>
                    <OutlinedInput
                        id="directory-groups-advanced-filter"
                        value={advancedFilter}
                        placeholder={groupsApi.advancedFilterPlaceholder}
                        onChange={(event) => setAdvancedFilter(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" && canRunAdvancedQuery) {
                                runAdvancedQuery();
                            }
                        }}
                        startAdornment={<InputAdornment position="start">
                            <Typography
                                variant="body2"
                                color="text.disabled"
                                sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}
                            >
                                {groupsApi.advancedFilterPrefix}
                            </Typography>
                        </InputAdornment>}
                        sx={{ fontFamily: "monospace" }}
                        inputProps={{ "aria-label": "Advanced group filter expression" }}
                    />
                </FormControl>
                <Button
                    id="run-directory-groups-query"
                    variant="contained"
                    disabled={!canRunAdvancedQuery}
                    onClick={runAdvancedQuery}
                    sx={{ whiteSpace: "nowrap" }}
                >
                    Run query
                </Button>
            </Box>

            {translation?.error && <Box sx={{ marginTop: "8px" }}>
                <Typography variant="body2" color="error">{translation.error.message}</Typography>
                {translation.error.offset >= 0 && <Box
                    component="pre"
                    sx={{ fontFamily: "monospace", fontSize: "12px", margin: "4px 0 0", color: "text.secondary", overflowX: "auto" }}
                >
                    {advancedFilter}
                    {"\n"}
                    {" ".repeat(groupsApi.filterErrorIndex(advancedFilter, translation.error.offset))}^
                </Box>}
            </Box>}

            {translation?.ldapFilter && <Box
                display="flex"
                gap="8px"
                flexWrap="wrap"
                alignItems="baseline"
                sx={{ marginTop: "8px" }}
            >
                <Typography variant="body2" color="text.secondary">Translated LDAP filter:</Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                    {translation.ldapFilter}
                </Typography>
            </Box>}

            <Typography variant="body2" color="text.secondary" sx={{ marginTop: "8px" }}>
                Groups are listed by their sAMAccountName, which can differ from the Name attribute a filter matches on.
            </Typography>
        </Box>}

        {error && <Alert severity="error" sx={{ marginBottom: "12px" }}>{error}</Alert>}
        {searchRequired && <Alert
            severity="info"
            sx={{ marginBottom: "12px" }}
            action={<Button size="small" onClick={() => setSortBy("default")}>
                Browse in directory order
            </Button>}
        >
            <AlertTitle>This directory has too many groups to sort</AlertTitle>
            Its domain controller will not order a result set this large, so {sortBy === "recent"
                ? "a newest-first"
                : "an alphabetical"} listing is not available. {advancedMode
                ? "Narrow your filter, or browse in directory order instead."
                : "Narrow it down with a search term, a category or a scope above, or browse in directory order instead."}
        </Alert>}
        {!searchRequired && <DataTable
            data={groups}
            finishedLoading={!loading}
            columnsState={columnsState}
            columnsDataId="datagrid_directory_groups_columns"
            noDataText={advancedMode
                ? appliedAdvancedFilter
                    ? "No groups match this filter."
                    : "Write a filter above, then select Run query."
                : filtersActive
                    ? "No groups match these filters."
                    : debouncedSearch ? "No groups match this search." : "No groups found in this directory."}
            columns={{
                included: {
                    displayName: "Included",
                    render: (group) => <Checkbox
                        checked={group.included}
                        disabled={saving}
                        onChange={(_, checked) => setIncluded(group, checked)}
                        inputProps={{ "aria-label": `Include ${group.name}` }}
                    />,
                },
                name: { displayName: "Group name", render: (group) => group.name },
            }}
        />}
        {!loading && !searchRequired && groups.length > 0 && <Box display="flex" alignItems="center" justifyContent="flex-end" sx={{ marginTop: "8px" }}>
            <Typography variant="body2" color="text.secondary">Entries per page:</Typography>
            <FormControl size="small" sx={{ marginLeft: "8px", minWidth: "90px" }}>
                <Select value={take} onChange={(event) => setTake(Number(event.target.value))}>
                    {[25, 50, 100, 200].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                </Select>
            </FormControl>
            <Typography sx={{ marginLeft: "20px" }}>Page {pageIndex + 1}</Typography>
            <IconButton
                aria-label="Previous page"
                disabled={pageIndex === 0}
                onClick={() => setPageIndex((current) => Math.max(0, current - 1))}
            >
                <NavigateBeforeIcon />
            </IconButton>
            <IconButton aria-label="Next page" disabled={!nextCursor} onClick={nextPage}>
                <NavigateNextIcon />
            </IconButton>
        </Box>}
    </>;
}
